import React, { forwardRef, ReactElement } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: "primary";
  renderLeftIcon?: ReactElement;
  renderRightIcon?: ReactElement;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

const generateClasses = (
  disabled: boolean,
  variant: ButtonProps["variant"],
  fullWidth: boolean,
  className
) => {
  let baseStyles =
    "flex justify-center items-center text-sm leading-[17px] px-4 whitespace-nowrap smallMobile:w-full h-9";

  baseStyles = clsx(
    baseStyles,
    fullWidth ? "w-full" : "grow-0",
    className && !className
  );

  const disabledOpacity = "opacity-50";
  if (!variant) {
    return clsx(baseStyles, { "opacity-50": disabled }, className);
  }

  if (variant === "primary") {
    const basePrimaryStyles =
      "py-2 h-[50px] font-medium rounded-lg text-white bg-gradient-to-r from-accent to-accent-bright border border-edge shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300";
    if (disabled) {
      return clsx(baseStyles, basePrimaryStyles, disabledOpacity);
    }
    return clsx(baseStyles, basePrimaryStyles, className);
  }
};

const getLeftIcon = (renderLeftIcon: ButtonProps["renderLeftIcon"]) => {
  if (!renderLeftIcon) return null;
  return (
    <div className="mr-2 text-xs" data-testid="left-icon">
      {renderLeftIcon}
    </div>
  );
};

const getRightIcon = (renderRightIcon: ButtonProps["renderRightIcon"]) => {
  if (!renderRightIcon) return null;
  return (
    <div className="ml-2 text-xs" data-testid="right-icon">
      {renderRightIcon}
    </div>
  );
};

const CustomButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      loading = false,
      variant,
      disabled = false,
      children,
      renderRightIcon,
      renderLeftIcon,
      className,
      fullWidth = false,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const classes = generateClasses(disabled, variant, fullWidth, className);
    return (
      <button
        {...props}
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        data-testid="button"
        aria-disabled={disabled || loading}
      >
        {loading ? (
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        ) : (
          <>
            {getLeftIcon(renderLeftIcon)}

            <p>{children}</p>
            {getRightIcon(renderRightIcon)}
          </>
        )}
      </button>
    );
  }
);

export default CustomButton;
